import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import type { ChildRunApproval } from '../../../contracts/child-run-approval.types';
import type { ChatItem, ToolUseItem } from '../../../contracts/harness.types';
import type { ComputerUseExecutorPort } from '../../../contracts/projects/computer-use-run-control.types';
import type { RunTransport, StreamHeadersFactory } from '../../../contracts/transport';
import { RUN_COMPUTER_USE_TOOL_NAME } from '../../harness/timeline/derive-is-computer-use';
import { resolveDesktopToolCancel } from '../components/ProjectChat.helpers';
import type { DelegateTraceEntry } from '../lib/computer-use-trace.types';
import { findInFlightToolUses, isDesktopTool } from '../lib/desktop-tools';
import { useSubAgentComputerUseProgress } from './useSubAgentComputerUseProgress';

/**
 * Live progress for a single in-flight `run_computer_use_task` call,
 * scoped to its `tool_call_id`. Fed from the delegate hook so the
 * matching `ComputerUseTaskBlock` shows the real planner/executor step +
 * ladder instead of a generic shimmer.
 */
export interface LiveDelegateStep {
  toolCallId: string;
  label: string;
  count: number;
  trace?: DelegateTraceEntry[];
  /** Child-run approval, shown on the task card as well as in /cua-status. */
  childRunApproval?: ChildRunApproval | null;
  onCancel?: () => void;
  /**
   * The user stopped this LOCAL desktop task and the cancelled
   * tool-result hasn't landed yet. The block renders a disabled
   * "Stopping…" Stop instead of an active one while the harness
   * timeline still reports the tool as running.
   */
  cancelRequested?: boolean;
}

/**
 * What the app supplies: the executor's stop and stop signal, and the run
 * transport the child's progress streams over. Pass one module-level object, so
 * the hook port keeps its identity and the stop stays stable across renders.
 */
export interface ProjectDesktopDelegationPorts extends ComputerUseExecutorPort {
  transport: RunTransport;
  /** The Scribe header factory, used when the transport carries none. */
  streamHeaders: StreamHeadersFactory;
}

const isHarnessOwnedComputerUse = (item: ToolUseItem): boolean =>
  item.isComputerUse && item.isSubAgent === true;

const isExecutingDesktopToolUse = (item: ToolUseItem): boolean => {
  if (!item.isComputerUse) {
    return isDesktopTool(item.name);
  }
  if (item.isSubAgent === true) {
    return item.status === 'running';
  }
  return (
    item.name.toLowerCase() === RUN_COMPUTER_USE_TOOL_NAME && item.status === 'running'
  );
};

interface UseProjectDesktopDelegationArgs {
  items: ChatItem[];
  turnInFlight: boolean;
  cancelParentTurn: () => void;
  ports: ProjectDesktopDelegationPorts;
}

export const useProjectDesktopDelegation = ({
  items,
  turnInFlight,
  cancelParentTurn,
  ports,
}: UseProjectDesktopDelegationArgs): {
  inFlightDesktopCall: ToolUseItem | undefined;
  liveDelegateStep: LiveDelegateStep | undefined;
  stopTurn: () => void;
  /**
   * True while a chat-side Stop (composer/Esc or in-transcript card) propagates
   * for an in-flight desktop task, before the parent lifecycle reports its
   * cancelling state. An overlay-origin child-only cancel does NOT set this.
   */
  wrappingUp: boolean;
} => {
  const { cancelComputerUseRun, useIsExecutorStopSignalled, transport, streamHeaders } =
    ports;
  // NOTE: the Rust executor attach is owned solely by the app-shell host
  // (ProjectsBackgroundRunner). A second attach instance for the same parent
  // run would trample the host's in-flight attach state and hang the run, so
  // delegation must never mount it.

  const inFlightDesktopCall = useMemo<ToolUseItem | undefined>(() => {
    return findInFlightToolUses(items).find(isExecutingDesktopToolUse);
  }, [items]);

  // A desktop-tool cancel (local OR sub-agent) leaves the harness timeline
  // reporting the tool ``running`` until the cancel settles, so without an
  // optimistic latch both the composer Stop and the in-transcript block
  // Stop keep reading as fresh, clickable actions. Latch the cancelled
  // in-flight ``tool_call_id`` (so a *second* desktop task in the same turn
  // isn't mislabelled "stopping"); clear when the parent turn settles.
  //
  // Read the id from a ref so ``latchInFlightCancel`` stays stable across the
  // per-tick ``inFlightDesktopCall`` churn (else the block's ``onCancel`` re-keys).
  const [cancelledToolCallId, setCancelledToolCallId] = useState<string | null>(null);
  const inFlightDesktopCallIdRef = useRef(inFlightDesktopCall?.id);
  inFlightDesktopCallIdRef.current = inFlightDesktopCall?.id;
  const latchInFlightCancel = useCallback(() => {
    const id = inFlightDesktopCallIdRef.current;
    if (id) setCancelledToolCallId(id);
  }, []);
  useEffect(() => {
    if (!turnInFlight) setCancelledToolCallId(null);
  }, [turnInFlight]);

  // Scope optimistic wrap-up state to the desktop task that was stopped. The
  // timeline can keep reporting that task as ``running`` briefly while child
  // and parent cancellation propagate.
  const cancelledToolIsCurrent =
    cancelledToolCallId !== null &&
    (!inFlightDesktopCall || inFlightDesktopCall.id === cancelledToolCallId);

  const subAgentChildRunId =
    inFlightDesktopCall && isHarnessOwnedComputerUse(inFlightDesktopCall)
      ? inFlightDesktopCall.childAgentRunId
      : undefined;
  const subAgentProgress = useSubAgentComputerUseProgress(subAgentChildRunId, transport, {
    streamHeaders,
  });

  // Overlay-origin cancels (the /cua-status X) don't route through this hook;
  // they stop the same app-wide executor slot, so read it here too.
  const executorStopSignalled = useIsExecutorStopSignalled(subAgentChildRunId);

  // Run a routed desktop-tool cancel.
  // Shared by BOTH cancel surfaces: the composer/Esc Stop (``stopTurn``) and
  // the in-transcript "Stop desktop task" button (``liveDelegateStep.onCancel``).
  const runDesktopToolCancel = useCallback(
    (routed: (() => void) | undefined) => {
      const cancel = routed ?? cancelParentTurn;
      // Latch here for instant feedback, and because it's the ONLY signal for a
      // parent-turn cancel (``cancelParentTurn`` never touches the executor
      // slot). Overlay-origin cancels arrive via ``executorStopSignalled``.
      latchInFlightCancel();
      cancel();
    },
    [cancelParentTurn, latchInFlightCancel]
  );

  const liveDelegateStep = useMemo<LiveDelegateStep | undefined>(() => {
    const inFlightCall = inFlightDesktopCall;
    if (!inFlightCall) return undefined;
    const isSubAgentDesktopCall = isHarnessOwnedComputerUse(inFlightCall);
    const routed = resolveDesktopToolCancel(
      isSubAgentDesktopCall,
      inFlightCall.childAgentRunId,
      cancelParentTurn,
      cancelComputerUseRun
    );
    const onCancel = routed ? () => runDesktopToolCancel(routed) : undefined;
    // The harness keeps reporting this tool ``running`` until the cancel
    // settles, so flag the optimistic "stopping" state (local latch or the
    // app-wide executor signal) for the block to render a disabled Stop.
    const cancelRequested =
      inFlightCall.id === cancelledToolCallId || executorStopSignalled;

    if (isSubAgentDesktopCall) {
      return {
        toolCallId: inFlightCall.id,
        label: subAgentProgress.liveStep ?? '',
        count: subAgentProgress.liveStepCount,
        trace: subAgentProgress.trace,
        childRunApproval: subAgentProgress.childRunApproval,
        onCancel,
        cancelRequested,
      };
    }

    // The local JS delegate is gone, so non-sub-agent rows have no live
    // progress. Composer Stop cancels the parent turn.
    return {
      toolCallId: inFlightCall.id,
      label: '',
      count: 0,
      trace: [],
      onCancel,
      cancelRequested,
    };
  }, [
    cancelComputerUseRun,
    cancelParentTurn,
    cancelledToolCallId,
    executorStopSignalled,
    inFlightDesktopCall,
    runDesktopToolCancel,
    subAgentProgress,
  ]);

  // The chat-input Stop cancels the parent turn and relies on the BE cascade.
  // The in-transcript card additionally stops its child executor first for
  // prompt native shutdown. The overlay X remains child-only via its own path.
  const stopTurn = useCallback(() => {
    runDesktopToolCancel(cancelParentTurn);
  }, [cancelParentTurn, runDesktopToolCancel]);

  return {
    inFlightDesktopCall,
    liveDelegateStep,
    stopTurn,
    wrappingUp: cancelledToolIsCurrent && turnInFlight,
  };
};
